import { Injectable, NgZone } from '@angular/core';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { File } from '@ionic-native/file/ngx';
import { Platform, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateConfigService } from './translate-config.service';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

@Injectable({
	providedIn: 'root'
})
export class CheckoutService {
	private activeBrowser: any = null;

	constructor(
		private iab: InAppBrowser,
		private file: File,
		public plt: Platform,
		private router: Router,
		private toastController: ToastController,
		private translateConfigService: TranslateConfigService,
		private ngZone: NgZone
	) { }

	private closeActiveBrowser() {
		if (this.activeBrowser) {
			try {
				this.activeBrowser.close();
			} catch (e) {
				console.log('Error cerrando browser previo:', e);
			}
			this.activeBrowser = null;
		}
	}


	async checkout_tpv(data: any, tpvWindow?: Window | null): Promise<void> {
		// Solución para compilación en navegador / angular web
		if (Capacitor.getPlatform() === 'web') {
			if (tpvWindow) {
				// Ventana pre-abierta síncronamente en el contexto del click: no hay bloqueo de popup
				let formHtml = '<form id="f" method="POST" action="' + data.url + '">';
				for (const key in data.data) {
					if (Object.prototype.hasOwnProperty.call(data.data, key)) {
						formHtml += '<input type="hidden" name="' + key + '" value="' + data.data[key] + '">';
					}
				}
				formHtml += '<\/form><script>document.getElementById("f").submit();<\/script>';
				tpvWindow.document.write(formHtml);
			} else {
				// Fallback: comportamiento anterior
				const form = document.createElement('form');
				form.style.display = 'none';
				form.method = 'POST';
				form.action = data.url;
				form.target = '_blank';

				for (const key in data.data) {
					if (Object.prototype.hasOwnProperty.call(data.data, key)) {
						const input = document.createElement('input');
						input.type = 'hidden';
						input.name = key;
						input.value = data.data[key];
						form.appendChild(input);
					}
				}

				document.body.appendChild(form);
				form.submit();

				setTimeout(() => {
					if (document.body.contains(form)) {
						document.body.removeChild(form);
					}
				}, 500);
			}

			// Redirigimos a home en la pestaña original
			this.router.navigate(['/tabs/inicio/home']);
			return;
		}

		let filePath = '';

		// Para dispositivos nativos (Capacitor)
		if (this.plt.is('capacitor') || Capacitor.isNativePlatform()) {
			console.log('Platform detected:', this.plt.platforms());
			if (this.plt.is('android')) {
				filePath = 'file:///android_asset/public/assets/html/redirect.html';
			} else if (this.plt.is('ios')) {
				try {
					// InAppBrowser en iOS requiere una URL absoluta con esquema nativo (file://).
					// Escribimos redirect.html en la caché del dispositivo para obtener una ruta file:// válida.
					const result = await Filesystem.writeFile({
						path: 'redirect.html',
						data: this.buildRedirectHtml(data),
						directory: Directory.Cache,
						encoding: Encoding.UTF8
					});
					filePath = result.uri;
				} catch (e) {
					console.error('Error escribiendo redirect.html en cache de iOS:', e);
					filePath = Capacitor.convertFileSrc('assets/html/redirect.html');
				}
			} else {
				filePath = Capacitor.convertFileSrc('assets/html/redirect.html');
			}
			console.log('Capacitor file path:', filePath);
		}
		// Para Cordova (fallback)
		else if (this.plt.is('cordova')) {
			filePath = this.file.applicationDirectory + 'www/assets/html/redirect.html';
			console.log('Cordova file path:', filePath);
		}
		else {
			filePath = '/assets/html/redirect.html';
		}

		// Construir script para inyectar usando JSON.stringify para escapar correctamente
		const formDataJson = JSON.stringify(data.data);
		const urlJson = JSON.stringify(data.url);
		const payScript = `
			(function() {
				try {
					if (window.__tpvSubmitted) {
						console.log('Formulario TPV ya enviado');
						return;
					}
					var form = document.getElementById('tpvRedirect');
					if (!form) {
						console.error('Formulario tpvRedirect no encontrado');
						return;
					}
					var data = ${formDataJson};
					form.innerHTML = '';
					for (var key in data) {
						if (Object.prototype.hasOwnProperty.call(data, key)) {
							var input = document.createElement('input');
							input.type = 'hidden';
							input.name = key;
							input.value = String(data[key] ?? '');
							form.appendChild(input);
						}
					}
					form.action = ${urlJson};
					form.method = 'POST';
					console.log('Enviando formulario TPV...');
					window.__tpvSubmitted = true;
					form.submit();
				} catch (e) {
					console.error('Error en script TPV:', e);
					if (window.showRedirectFallback) {
						window.showRedirectFallback('No se pudo iniciar el pago automaticamente.');
					}
				}
			})();
		`;

		// Cerrar instancia anterior si existe (evita fugas de listeners y ventanas fantasma)
		this.closeActiveBrowser();

		const browser = this.iab.create(filePath, '_blank', 'location=no,EnableViewPortScale=yes');
		this.activeBrowser = browser;
		console.log('InAppBrowser abierto con:', filePath);

		// Flag para evitar manejar el resultado dos veces
		let hasHandledResult = false;

		const handlePaymentResult = (url: string) => {
			if (hasHandledResult) return;
			hasHandledResult = true;

			console.log('Detectado url de finalización, cerrando navegador. URL:', url);
			browser.close();
			this.activeBrowser = null;

			// Ejecutar navegación dentro de Angular zone para evitar warnings
			this.ngZone.run(() => {
				if (url.includes('status=ko')) {
					this.showErrorToast();
				} else {
					this.showSuccessToast();
				}
				this.router.navigate(['/tabs/inicio/home']);
			});
		};

		browser.on('loadstop').subscribe(
			(event) => {
				console.log('Loadstop URL:', event.url);
				if (event.url.includes('arriva.gal') || event.url.includes('arriva.es')) {
					handlePaymentResult(event.url);
				}
				else if (event.url.includes('redirect.html')) {
					// Inyectar script cuando la página redirect.html ha cargado
						browser.executeScript({ code: payScript }).then(
							() => console.log('Script TPV inyectado correctamente'),
							(err) => {
								console.error('Error inyectando script TPV:', err);
								browser.executeScript({
									code: "if (window.showRedirectFallback) { window.showRedirectFallback('No se pudo iniciar el pago automaticamente. Pulsa continuar para reintentar.'); }"
								});
							}
						);
					}
				else if (event.url.includes('tpv') || event.url.includes('ceca')) {
					console.log('Navegando en TPV:', event.url);
				}
			},
			(err) => {
				console.log('InAppBrowser Loadstop Event Error: ' + err);
			}
		);
		browser.on('loadstart').subscribe(
			(event) => {
				if (event.url.includes('arriva.gal') || event.url.includes('arriva.es')) {
					handlePaymentResult(event.url);
				}
			},
			(err) => {
				console.log('InAppBrowser Loadstart Event Error: ' + err);
			}
		);
		browser.on('exit').subscribe(
			() => {
				console.log('InAppBrowser cerrado');
				this.activeBrowser = null;
			}
		);
	}

	private escapeHtml(str: any): string {
		return String(str ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	private buildRedirectHtml(data: any): string {
		let inputsHtml = '';
		if (data && data.data) {
			for (const key in data.data) {
				if (Object.prototype.hasOwnProperty.call(data.data, key)) {
					const escapedKey = this.escapeHtml(key);
					const escapedVal = this.escapeHtml(data.data[key]);
					inputsHtml += `        <input type="hidden" name="${escapedKey}" value="${escapedVal}">\n`;
				}
			}
		}
		const formAction = this.escapeHtml(data?.url ?? '');

		return `<!DOCTYPE html>
<html>
<head>
    <title>Redirecting...</title>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, Arial, sans-serif; margin: 0; padding: 24px; color: #222; text-align: center; }
        p { margin: 0 0 12px 0; }
        #fallbackContainer { display: none; margin-top: 16px; }
        #fallbackButton { padding: 10px 14px; border: 0; background: #d81f26; color: #fff; border-radius: 4px; font-size: 16px; cursor: pointer; }
        #fallbackMessage { color: #444; }
    </style>
</head>
<body>
    <form id="tpvRedirect" method="POST" action="${formAction}">
        <p>Redirigiendo a la pasarela de pago...</p>
${inputsHtml}    </form>
    <div id="fallbackContainer">
        <p id="fallbackMessage">No se pudo continuar automáticamente.</p>
        <button id="fallbackButton" type="button">Continuar</button>
    </div>
    <script>
        (function () {
            var form = document.getElementById('tpvRedirect');
            var container = document.getElementById('fallbackContainer');
            var message = document.getElementById('fallbackMessage');
            var button = document.getElementById('fallbackButton');

            window.showRedirectFallback = function (text) {
                if (text && message) {
                    message.textContent = text;
                }
                if (container) {
                    container.style.display = 'block';
                }
            };

            if (button && form) {
                button.addEventListener('click', function () {
                    try {
                        form.submit();
                    } catch (e) {
                        window.showRedirectFallback('No se pudo enviar el formulario. Cierra esta ventana e inténtalo de nuevo.');
                    }
                });
            }

            try {
                if (form) {
                    window.__tpvSubmitted = true;
                    form.submit();
                }
            } catch (e) {
                console.error('Error auto-submitting form:', e);
                if (window.showRedirectFallback) {
                    window.showRedirectFallback('Pulsa continuar para acceder al pago.');
                }
            }
        })();
    </script>
</body>
</html>`;
	}

	private async showSuccessToast() {
		const toast = await this.toastController.create({
			message: this.translateConfigService.getKey('COMPRAR.exito'),
			duration: 3000,
			color: 'success'
		});
		toast.present();
	}

	private async showErrorToast() {
		const toast = await this.toastController.create({
			message: this.translateConfigService.getKey('COMPRAR.error'),
			duration: 3000,
			color: 'danger'
		});
		toast.present();
	}





}

